import { EMPTY_ARRAY } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import {
  getDateRangeWindow,
  getIdOrLabelFromCategory,
} from '@modules/common/utils';
import { useGetAnomaliesGridColumns } from '@modules/quantify/components/QuantifyAnomaliesTableView/hooks/useGetAnomaliesGridColumns';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { getQuantifyQueryContainer } from '@modules/quantify/utils';
import { Box } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { useToggle } from 'react-use';
import { UUID } from 'v2/core/entities/uuid';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface QuantifyAnomaliesTableViewGridProps {
  anomalousTimestamp: number;
  containerHeight: number;
  shouldShowAddToFilter?: boolean;
}

export const QuantifyAnomaliesTableViewGrid = ({
  anomalousTimestamp,
  containerHeight,
  shouldShowAddToFilter = true,
}: QuantifyAnomaliesTableViewGridProps) => {
  const tableRows = useQuantifyStore(
    ({ reportData }) => reportData.quantify.tableRows,
  );
  const query = useQuantifyStore(({ query }) => query);
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause ?? EmptyWhereClause,
  );
  const aggregatePeriod = useQuantifyStore(
    ({ metadata }) => metadata.aggregatePeriod,
  );

  const [selectedMetricKeyId, setSelectedMetricKeyId] = useState('');
  const [selectedMetricKeyLabel, setSelectedMetricKeyLabel] = useState('');
  const [selectedMetricKeyCategory, setSelectedMetricKeyCategory] =
    useState<Category>();
  const [isSelectedMetricKeyInvisible, setIsSelectedMetricKeyInvisible] =
    useState(false);
  const [
    isSelectedMetricKeyUpdateInProgress,
    setIsSelectedMetricKeyUpdateInProgress,
  ] = useState(false);

  const [feedbackQueryContainer, setFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);
  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);

  const showFeedbackDrawer = (params: {
    id: UUID;
    label: string;
    category?: Category;
    isInvisible: boolean;
    isUpdateInProgress: boolean;
  }) => {
    toggleShouldOpenFeedbackDrawer(true);
    setSelectedMetricKeyId(params.id);
    setSelectedMetricKeyLabel(params.label);
    setSelectedMetricKeyCategory(params.category);
    setIsSelectedMetricKeyInvisible(params.isInvisible);
    setIsSelectedMetricKeyUpdateInProgress(params.isUpdateInProgress);

    const queryContainer = getQuantifyQueryContainer({
      query,
      metricKeyId: params.id,
      metricCategory: params.category,
      dateRange: getDateRangeWindow({
        timestamp: anomalousTimestamp,
        aggregatePeriod,
      }),
      whereClause,
    });

    setFeedbackQueryContainer(queryContainer);
  };

  const columns = useGetAnomaliesGridColumns({
    onRecordsCountClick: showFeedbackDrawer,
    anomalousTimestamp,
  });

  return (
    <Box
      sx={{
        px: 1,
        height: containerHeight ? `${containerHeight}px` : '350px',
        width: '100%',
      }}
    >
      <DataGridPro
        sx={{
          border: 'none',
          '& .MuiDataGrid-row': {
            '&: hover': {
              '.js-add-to-filter-action': {
                opacity: 1,
              },
            },
          },
        }}
        rows={tableRows ?? EMPTY_ARRAY}
        columns={columns}
        getRowId={({ id, category }) =>
          getIdOrLabelFromCategory({
            key: id,
            category,
          })
        }
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 40}
        columnHeaderHeight={45}
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        columnVisibilityModel={{
          addToFilterAction: shouldShowAddToFilter,
        }}
      />
      {feedbackQueryContainer && (
        <QuantifyFeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={toggleShouldOpenFeedbackDrawer}
          queryContainer={feedbackQueryContainer}
          metricKeyLabel={selectedMetricKeyLabel}
          metricKeyId={selectedMetricKeyId}
          metricKeyCategory={selectedMetricKeyCategory}
          isMetricKeyInvisible={isSelectedMetricKeyInvisible}
          isMetricKeyUpdateInProgress={isSelectedMetricKeyUpdateInProgress}
        />
      )}
    </Box>
  );
};
